import { AnchorButton, Classes, InputGroup, Intent } from '@blueprintjs/core';
import * as React from 'react';
import { Route } from 'react-router-dom';
import { Dialog, VoiceboxLogo } from 'vet-bones/components';

import { refreshDatabases } from 'src/actions/databases/action-creators/refreshDatabases';
import { ROUTE } from 'src/constants/routes';
import { VoiceboxDialogConversation } from 'src/containers/Voicebox/VoiceboxDialogConversation';
import { diagramSnapshotSelector } from 'src/selectors/diagramSnapshot';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { voiceboxModeSelector } from 'src/selectors/voicebox';
import { uiSlice } from 'src/store/ui/ui.slice';
import { VoiceboxMode } from 'src/store/voicebox/types';
import { voiceboxSlice } from 'src/store/voicebox/voicebox.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useSetupDefaultPortalConnection } from 'src/utils/hooks/useSetupDefaultPortalConnection';
import { createVoiceboxModelSchema } from 'src/utils/voicebox/createVoiceboxModelSchema';

const VoiceboxDialogBody: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();
  const diagram = useReduxSelector(diagramSnapshotSelector(projectId));

  useSetupDefaultPortalConnection({ shouldNotNotifySuccess: true });

  const isConnected = useReduxSelector(isConnectedSelector);
  React.useEffect(() => {
    if (isConnected) {
      dispatch(refreshDatabases());
    }
  }, [dispatch, isConnected]);

  const mode = useReduxSelector(voiceboxModeSelector);
  const initialOpenAiKey = useReduxSelector(
    (state) => state.voicebox.openAiKey
  );

  const [openAiKey, setOpenAiKey] = React.useState(initialOpenAiKey);

  const modelSchema = React.useMemo(() => {
    if (!diagram) {
      return undefined;
    }

    return createVoiceboxModelSchema({
      model: diagram.model,
    });
  }, [diagram]);

  const setMode = React.useCallback(
    (mode: VoiceboxMode) => {
      if (openAiKey) {
        if (mode === VoiceboxMode.EXISTING && modelSchema) {
          dispatch(
            voiceboxSlice.actions.setVoiceboxMode({
              mode,
              modelSchema,
              openAiKey,
            })
          );
        } else if (mode === VoiceboxMode.NEW) {
          dispatch(voiceboxSlice.actions.setVoiceboxMode({ mode, openAiKey }));
        }
      }
    },
    [dispatch, modelSchema, openAiKey]
  );

  if (openAiKey && mode) {
    return <VoiceboxDialogConversation />;
  }

  const existingDisabled = Boolean(
    modelSchema && !modelSchema.classIris.length
  );

  const disabled = !openAiKey;

  return (
    <div className="sd-voicebox__setup">
      <div>
        <span>
          Your OpenAI API key, which can be found{' '}
          <a
            href="https://platform.openai.com/account/api-keys"
            rel="noreferrer"
            target="_blank"
          >
            here
          </a>
          :
        </span>
        <form>
          <InputGroup
            autoComplete="off"
            autoFocus
            onChange={(evt) => {
              setOpenAiKey(evt.currentTarget.value);
            }}
            type="password"
            value={openAiKey}
          />
        </form>
      </div>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-evenly',
        }}
      >
        <AnchorButton
          disabled={disabled}
          intent={Intent.PRIMARY}
          onClick={() => setMode(VoiceboxMode.NEW)}
          text="New model"
        />
        <AnchorButton
          disabled={disabled || existingDisabled}
          intent={Intent.PRIMARY}
          onClick={() => setMode(VoiceboxMode.EXISTING)}
          text="Existing model"
        />
      </div>
    </div>
  );
};

export const VoiceboxDialog: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const isOpen = useReduxSelector((state) => state.ui.isVoiceboxDialogOpen);

  const handleClose = React.useCallback(
    () => dispatch(uiSlice.actions.toggledVoiceboxDialog({ isOpen: false })),
    [dispatch]
  );

  return (
    <Dialog
      canEscapeKeyClose
      canOutsideClickClose
      className="sd-dialog-voicebox"
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      portalClassName="sd-dialog-voicebox-portal"
      title={
        <>
          <VoiceboxLogo size={24} />
          {copy.components.voicebox.title}
        </>
      }
    >
      <Route path={ROUTE.PROJECT}>
        <div className={Classes.DIALOG_BODY}>
          <VoiceboxDialogBody />
        </div>
      </Route>
    </Dialog>
  );
};
